import { useState, useEffect, useCallback } from 'react';
import './WeatherData.css';
import RenderClock from './RenderClock';
import CitySelectionModal from './CitySelectionModal';
import { cities } from './cities';

function WeatherData() {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [errorWakeLock, setErrorWakeLock] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCity, setSelectedCity] = useState(() => {
    // Initialize with a valid city from localStorage or default to 'Ljubljana'
    const savedCity = localStorage.getItem('selectedCity');
    if (savedCity && cities.includes(savedCity)) {
      return savedCity;
    }
    return 'Ljubljana';
  });

  const wakeLock = useCallback(async () => {
    try {
      await navigator.wakeLock.request('screen');
      setErrorWakeLock('Wake Lock is active');
    } catch (err) {
      setErrorWakeLock(`${err.name}, ${err.message}`);
    }
  }, []);

  const fetchData = useCallback(() => {
    if (!selectedCity) {
      console.log('No city selected, skipping fetch');
      return;
    }

    var xhr = new XMLHttpRequest();
    xhr.open('GET', `https://vreme.u3pm.com/api/weather/${selectedCity}`, true);

    xhr.onload = function () {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          var response = JSON.parse(xhr.responseText);
          if (response && response.data) {
            const data = {
              ...response.data,
              forecast: groupTodaysAndForecast(response.data.todays || [], cleanForecast(response.data.forecast || [])),
            };
            setData(data);
            document.querySelector('.main').style.visibility = 'visible';
          } else {
            console.log('Invalid response structure');
          }
        } catch (e) {
          console.log('ERROR--------->', e);
        }
      } else if (xhr.status === 401 || xhr.status === 403) {
        // Redirect to Google authentication
        console.log('Redirecting to Google authentication');
        window.location.href = '/auth/google';
      } else {
        console.log('Unknown error', xhr.status);
      }
      setIsLoading(false);
    };

    xhr.send();
  }, [selectedCity]);

  useEffect(() => {
    const videoElement = document.createElement('video');
    videoElement.src = '/muted.mp4';
    videoElement.loop = true;
    videoElement.muted = true;
    videoElement.play();
    wakeLock();
    setMainFontSize();
    window.addEventListener('resize', setMainFontSize);
    setIsLoading(true);

    return () => {
      window.removeEventListener('resize', setMainFontSize);
    };
  }, [wakeLock]);

  useEffect(() => {
    // Only fetch data when we have a selected city
    if (selectedCity) {
      fetchData();
    }
  }, [selectedCity, fetchData]);

  useEffect(() => {
    // Set up interval only after initial data is loaded
    if (selectedCity) {
      const interval = setInterval(() => {
        fetchData();
      }, 1000 * 60 * 15);

      return () => clearInterval(interval);
    }
  }, [selectedCity, fetchData]);

  function setMainFontSize() {
    document.querySelector('.main').style.fontSize = `${
      (Math.min(window.innerWidth, window.innerHeight) / 768) * 25
    }px`;
  }

  function cleanForecast(forecast) {
    if (!forecast || !Array.isArray(forecast)) {
      return [];
    }
    const hours = [];
    return forecast.filter((obj) => {
      const f = hours.indexOf(obj.h) === -1;
      hours.push(obj.h);
      return f;
    });
  }

  function groupTodaysAndForecast(todays, forecast) {
    if (!todays || !Array.isArray(todays)) {
      todays = [];
    }
    if (!forecast || !Array.isArray(forecast)) {
      forecast = [];
    }
    const todaysFiltered = todays.filter((obj) => forecast.map((x) => x.h).indexOf(obj.h) === -1);
    return [...todaysFiltered, ...forecast];
  }

  function localHour(hour) {
    const offsetInHours = new Date().getTimezoneOffset() / 60;
    return (parseInt(hour) - offsetInHours + 24) % 24;
  }

  function renderHour(hour, values, index, minTemp, maxTemp) {
    const value = values.find((obj) => obj.h.substring(11) === hour);
    const classes = ['cell'];
    if (!value) {
      return <div className="cell" key={`hour-${hour}-${index}`}></div>;
    }
    if (value.h < new Date().toISOString().substring(0, 13)) {
      classes.push('disabled');
    }
    return (
      <div className={classes.join(' ')} key={`hour-${hour}-${index}`}>
        {value && (
          <>
            <div className="hour">{localHour(hour)}:00</div>
            <div className="temperature">
              <div
                className={`temperature-value ${parseInt(value.t) === minTemp && 'temp-blue'} ${
                  parseInt(value.t) === maxTemp && 'temp-yellow'
                }`}
              >
                {value.t}°
              </div>
            </div>
            <div className="icon-cell">
              <img src={`/svg/${value.i}.svg`} className="icon" alt="Weather icon" />
            </div>
            <div className="wind">
              {value.s && <img src={`/svg/${value.s}.svg`} className="wind-icon" alt="Wind direction" />}
              {value.w}
            </div>
          </>
        )}
      </div>
    );
  }

  function getSlovenianWeekday(date) {
    const weekdays = ['Ned', 'Pon', 'Tor', 'Sre', 'Čet', 'Pet', 'Sob'];
    return weekdays[date.getDay()];
  }

  function getSlovenianMonthAbbreviation(date) {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'Maj', 'Jun', 'Jul', 'Avg', 'Sep', 'Okt', 'Nov', 'Dec'];
    return months[date.getMonth()];
  }

  function renderDay(offsetDay, fontSize) {
    const date = new Date(offsetDay);
    return (
      <div className="day-row cell" key={`day-${offsetDay}`}>
        <div className="move-left">
          <div className="day-text" style={{ fontSize: `${fontSize}em` }}>
            <span className="date-weekday">{getSlovenianWeekday(date)}</span>{' '}
            <span className="date-day">{date.getDate()}</span>{' '}
            <span className="date-month">{getSlovenianMonthAbbreviation(date)}</span>
          </div>
        </div>
      </div>
    );
  }

  function showRows(dayOffset) {
    let everyNHour = 3;
    if (dayOffset === 0) {
      everyNHour = 2;
    }
    if (!data) {
      return null;
    }
    const today = new Date();
    const tomorrow = new Date(today);
    const offsetDay = new Date(tomorrow.setDate(today.getDate() + dayOffset)).toISOString().split('T')[0];

    // Calculate font size based on city name length
    const cityLength = selectedCity.length;
    let fontSize = 1.3; // Default for 11 characters or less
    if (cityLength > 11) {
      fontSize = Math.max(1.1, 1.3 - 0.2 * (cityLength - 11));
    }

    let minTemp = 100;
    let maxTemp = -100;
    const values = data.forecast
      .filter((obj) => {
        return obj.h.substring(0, 10) === offsetDay && parseInt(obj.h.substring(11)) % everyNHour === 0;
      })
      .sort((a, b) => parseInt(a.h.substring(11)) - parseInt(b.h.substring(11)));
    const day = [renderDay(offsetDay, fontSize)];
    return day.concat(
      [...Array(24 / everyNHour)]
        .map((_, idx) => {
          if (values[idx]) {
            if (parseInt(values[idx].t) < minTemp) {
              minTemp = parseInt(values[idx].t);
            }
            if (parseInt(values[idx].t) > maxTemp) {
              maxTemp = parseInt(values[idx].t);
            }
          }
          return (idx * everyNHour).toString().padStart(2, '0');
        })
        .map((h, index) => renderHour(h, values, index, minTemp, maxTemp))
    );
  }

  function renderCurrentTemperature() {
    if (data && data.current) {
      return <div className="current-weather-temperature__value">{data.current.t}°</div>;
    }
    return null;
  }

  function renderCurrentWeatherIcon() {
    if (data && data.current) {
      return (
        <div className="current-weather-icon-wrapper">
          <img src={`/svg/${data.current.i}.svg`} className="current-weather-icon" alt="Current weather" />
        </div>
      );
    }
    return null;
  }

  function renderSunriseSunset() {
    if (data && data.current) {
      return (
        <div className="sunrise-sunset">
          <div>
            <img src="/svg/sun.svg" className="sun-icon" alt="Sunrise" />
            <span>{data.current.v}</span>
          </div>
          <div>
            <img src="/svg/moon.svg" className="moon-icon" alt="Sunset" />
            <span>{data.current.z}</span>
          </div>
        </div>
      );
    }
  }

  function renderDayForecast(day) {
    if (!data || !data.daysForecast) {
      return null;
    }
    const date = new Date(day.d);

    // Calculate font size based on city name length
    const cityLength = selectedCity.length;
    let fontSize = 1.3; // Default for 11 characters or less
    if (cityLength > 11) {
      fontSize = Math.max(1.1, 1.3 - 0.2 * (cityLength - 11));
    }

    return (
      <div className="day-forecast" key={day.d}>
        <div className="day-forecast__day" style={{ fontSize: `${fontSize}em` }}>
          <span className="date-weekday">{getSlovenianWeekday(date)}</span>{' '}
          <span className="date-day">{date.getDate()}</span>{' '}
          <span className="date-month">{getSlovenianMonthAbbreviation(date)}</span>
        </div>
        <div className="day-forecast__bottom">
          <div className="day-forecast__bottom__temperatures">
            <div className="temp-yellow">{day.h}°</div>
            <div className="temp-blue">{day.l}°</div>
          </div>
          <div className="day-forecast__bottom__weather">
            <div className="forecast-day-icon">
              <img src={`/svg/${day.i}.svg`} className="icon" alt="Weather icon" />
            </div>
            <div className="flex-row">
              {day.v}
              <img src={`/svg/${day.s}.svg`} alt="Wind direction" className="wind-icon" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  function renderDaysForecast() {
    if (data && data.daysForecast) {
      return data.daysForecast
        .filter((day) => {
          const today = new Date();
          const d = day.d;
          const afterTomorrow = new Date(today.setDate(today.getDate() + 2)).toISOString().substring(0, 10);
          return d > afterTomorrow;
        })
        .filter((_, idx) => {
          return idx <= 5;
        })
        .map(renderDayForecast);
    }
  }

  function renderDayAndDate() {
    if (data && data.current) {
      const today = new Date();
      const cityLength = selectedCity.length;
      let fontSize = 1.3; // Default for 11 characters or less

      if (cityLength > 11) {
        fontSize = Math.max(1.1, 1.3 - 0.2 * (cityLength - 11));
      }

      return (
        <div className="city-date">
          <span className="date-parts" style={{ fontSize: `${fontSize}em` }}>
            <span className="date-weekday">{getSlovenianWeekday(today)}</span>
            <span className="date-day">{today.getDate()}</span>
            <span className="date-month">{getSlovenianMonthAbbreviation(today)}</span>
          </span>
          <span className="city-name" onClick={openCityModal} style={{ fontSize: `${fontSize}em` }}>
            {selectedCity}
          </span>
        </div>
      );
    }
  }

  function renderCurrentWind() {
    if (data && data.current) {
      return (
        <div className="current-wind">
          <img src={`/svg/${data.current.s}.svg`} className="wind-icon" alt="Wind direction" />
          {data.current.w}
        </div>
      );
    }
  }

  function renderCurrentHumidity() {
    if (data && data.current) {
      return (
        <div className="current-humidity">
          <img src="/svg/humidity.svg" className="humidity-icon" alt="Humidity" />
          {data.current.r}
        </div>
      );
    }
  }

  function handleCitySelect(city) {
    setSelectedCity(city);
    setIsModalOpen(false);
  }

  function openCityModal() {
    setIsModalOpen(true);
  }

  function renderAll() {
    if (isLoading) {
      return <div></div>;
    }
    return (
      <>
        <div className="header__right">
          <div>{renderSunriseSunset()}</div>
          <div className="header__today">
            <div className="current-temperature-and-icon">
              {renderCurrentTemperature()}
              {renderCurrentWeatherIcon()}
              <div className="wind-and-humidity">
                {renderCurrentWind()}
                {renderCurrentHumidity()}
              </div>
            </div>
          </div>
        </div>
        <div className="main__header">
          <div className="clock-wrapper">
            <RenderClock />
          </div>
          {renderDayAndDate()}
        </div>
        <div className="main-table-wrapper">
          <div className="main-table">
            <div>{showRows(0)}</div>
          </div>
          <div className="main-table">
            <div>{showRows(1)}</div>
          </div>
          <div className="main-table">
            <div>{showRows(2)}</div>
          </div>
        </div>
        <div className="bottom">
          <div className="bottom__forecast">{renderDaysForecast()}</div>
        </div>
      </>
    );
  }

  return (
    <div className="main">
      {/* <div
        style={{
          display: 'none',
          float: 'left',
          position: 'absolute',
          top: '50px',
          left: '40px',
          color: 'green',
          padding: '3px 6px',
        }}
      >
        {window.screen.width}x{window.screen.height} {errorWakeLock}
      </div> */}
      {renderAll()}
      <CitySelectionModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} onCitySelect={handleCitySelect} />
    </div>
  );
}

export default WeatherData;
